import { Dispatch } from 'redux';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { AsyncJob, AsyncJobService, AsyncJobStatus, AsyncJobType } from 'types';
import { selectBackgroundTasks } from './selectors';
import { RootState } from '../reducer';

type BackgroundTasksState = {
  tasks: AsyncJob[],
};

const initialState: BackgroundTasksState = {
  tasks: [],
};

export const getJobTitleByType = (type: AsyncJobType) => {
  if (type === AsyncJobType.CLEANUP_CS_ADMIN_TENANCIES) {
    return type;
  }

  switch (type) {
  case AsyncJobType.DATA_LOADER:
    return 'Data loading';
  case AsyncJobType.RECALCULATE_AGING_FACTOR:
    return 'Aging factor recalculation';
  case AsyncJobType.RECALCULATE_COMPOSITE_TAG:
    return 'Composite tag recalculation';
  case AsyncJobType.RECALCULATE_COMPOSITES:
    return 'Composite recalculation';
  case AsyncJobType.RECALCULATE_PLAN:
    return 'Plan recalculation';
  case AsyncJobType.UPDATE_CUSTOM_PERCENTILE:
    return 'Custom Percentile recalculation';
  case AsyncJobType.ADD_INDEXED_DATA_CUTS:
    return 'Indexed Composites recalculation';
  case AsyncJobType.REMOVE_INDEXED_DATA_CUTS:
    return 'Indexed Composites recalculation';
  case AsyncJobType.RESET_DEFAULT_CUTS:
    return 'Applying Default Cuts';
  case AsyncJobType.UPDATE_PLAN:
    return 'Updating plan';
  case AsyncJobType.EXPORT_DATA:
    return 'Data Exporting'
  case AsyncJobType.EXPORT_EXCEL:
    return 'Exporting Survey Participation';
  case AsyncJobType.MIGRATE_PLAN:
    return 'Create plan';
  case AsyncJobType.ACTIVATE_DRAFT_PLAN:
    return 'Plan activation';
  case AsyncJobType.CREATE_PAYMARKET:
    return 'Create PayMarket';
  case AsyncJobType.DELETE_LOAD:
    return 'Delete File';
  case AsyncJobType.DELETE_SURVEY:
    return 'Delete Survey';
  case AsyncJobType.RECALCULATE_COMPANY_JOB_STATS:
    return 'Company Job Stats recalculation';
  case AsyncJobType.RECALCULATE_PLAN_STATS:
    return 'Plan Stats recalculation';
  case AsyncJobType.RECALCULATE_SLOT_JOB:
    return 'Slot Job recalculation';
  case AsyncJobType.RECALCULATE_SURVEY_CUT_STATS:
    return 'Survey Cut Stats recalculation';
  case AsyncJobType.RECALCULATE_SURVEY_JOB_STATS:
    return 'Survey Job Stats recalculation';
  case AsyncJobType.SETUP_COMPANY:
    return 'Company Is Setting Up';
  case AsyncJobType.RECALCULATE_DATA_MODEL_COMPOSITE_STATS:
  case AsyncJobType.RECALCULATE_DATA_MODEL_WITH_AUTO_SPREAD:
    return 'Data Model Recalculation';
  case AsyncJobType.PUBLISH_SURVEY_REPLACEMENT:
    return 'Survey Replacement publishing';
  case AsyncJobType.APPLY_CUT_SELECTION_POLICY_SURVEY_REPLACEMENT:
    return 'Survey Replacement cuts update';
  case AsyncJobType.ACTUALIZE_COMPOSITE_DATA_CUT_REPLACEMENT:
    return 'Survey Replacement composite data cuts actualizing';
  case AsyncJobType.ACTUALIZE_SURVEY_JOB_MAPPING_REPLACEMENT:
    return 'Survey Replacement job mappings actualizing';
  case AsyncJobType.INITIATE_RECALCULATION:
    return 'Composites recalculation';
  case AsyncJobType.SYNC_DATAHUB:
    return 'Survey Data synchronization';
  case AsyncJobType.TEST_HRIS_URL:
    return 'HRIS URL Test';
  case AsyncJobType.GENERATE_JOB_DESCRIPTION:
    return 'Job Description';
  case AsyncJobType.APPLY_WEIGHTING_POLICY:
    return 'Apply Weighting Policy';
  case AsyncJobType.ADD_DEFAULT_DATA_CUTS_FOR_JOB_MAPPINGS:
    return 'Add default data mapping';
  case AsyncJobType.GET_SURVEY_DATA_BY_POLICY_RESPONSE:
    return 'Get Survey Data By Policy';
  case AsyncJobType.GET_DEFAULT_DATA_CUTS_RESPONSE:
    return 'Apply Pricing Policy';
  case AsyncJobType.PROCESS_SRW_CUT_SELECTION_RESPONSE:
    return 'Survey Replacement cuts update';
  case AsyncJobType.RUN_SEASON_SURVEY_REPLACEMENTS:
    return 'Survey Season Replacement Run';
  default:
    // If there is ts error, ensure all types handled by switch case
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const exhaustiveCheck: never = type;
    return type;
  }
};

export const setBackgroundTasks = createAction<AsyncJob[]>('BACKGROUND_TASKS/SET');

export const addBackgroundTaskAction = createAction<AsyncJob>('BACKGROUND_TASKS/ADD');

export const removeBackgroundTaskAction = createAction<number>('BACKGROUND_TASKS/REMOVE');

export const updateBackgroundTaskAction = createAction<AsyncJob>('BACKGROUND_TASKS/UPDATE');

export const reloadBackgroundTasksAction = createAction<AsyncJob[]>('BACKGROUND_TASKS/RELOAD');

export const addBackgroundTask = (task: AsyncJob) => (dispatch: Dispatch) => {
  dispatch(addBackgroundTaskAction(task));
};

export const updateBackgroundTask = (task: AsyncJob) => (dispatch: Dispatch) => {
  dispatch(updateBackgroundTaskAction(task));
};

export const removeBackgroundTask = (task: AsyncJob) => (dispatch: Dispatch) => {
  dispatch(removeBackgroundTaskAction(task.id));
};

export const reloadBackgroundTasks = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const tasks = selectBackgroundTasks(getState());

  if (tasks.length) {
    const rows = await AsyncJobService.getAsyncJobList({
      status: [AsyncJobStatus.SCHEDULED, AsyncJobStatus.IN_PROGRESS],
      ignorePagination: true,
    });

    dispatch(reloadBackgroundTasksAction(rows));
  }
};

export const backgroundTasksReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setBackgroundTasks, (state, action) => {
      state.tasks = action.payload;
    })
    .addCase(reloadBackgroundTasksAction, (state, action) => {
      state.tasks = state.tasks.filter(task => {
        return action.payload.find(({ id }) => id === task.id);
      });
    })
    .addCase(addBackgroundTaskAction, (state, action) => {
      const existingTask = state.tasks.find(task => task.id === action.payload.id);
      if (!existingTask) {
        state.tasks = [
          action.payload,
          ...state.tasks,
        ];
      }
    })
    .addCase(removeBackgroundTaskAction, (state, action) => {
      state.tasks = state.tasks.filter(task => task.id !== action.payload);
    })
    .addCase(updateBackgroundTaskAction, (state, action) => {
      const existingTask = state.tasks.find(task => task.id === action.payload.id);

      state.tasks = existingTask
        ? state.tasks.map(task => {
          if (task === existingTask) {
            return action.payload;
          }

          return task;
        })
        : [action.payload, ...state.tasks];
    })
  ;
});
